import {
  AffiliateStatusResponseSchema,
  RedeemAffiliateCodeResponseSchema,
  type AffiliateStatusResponse,
  type RedeemAffiliateCodeResponse,
  type RedeemAffiliateCodeError,
} from "@codway-dev/shared";
import { AuthService } from "./auth.service";

const API_BASE_URL = "/api";

async function authedFetch(
  path: string,
  init?: RequestInit
): Promise<Response> {
  const token = await AuthService.getShopifyToken();
  if (!token) throw new Error("No Shopify token available");
  return fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...init?.headers,
    },
  });
}

/**
 * Carries the contract error code from a failed redeem so the UI can show a
 * granular message without parsing strings (see RedeemAffiliateCodeErrorSchema).
 */
export class AffiliateRedeemError extends Error {
  constructor(
    public readonly code: RedeemAffiliateCodeError["code"] | "UNKNOWN",
    message: string
  ) {
    super(message);
    this.name = "AffiliateRedeemError";
  }
}

export const AffiliateService = {
  getStatus: async (): Promise<AffiliateStatusResponse> => {
    const res = await authedFetch("/admin/affiliate/status");
    if (!res.ok) {
      throw new Error(`Failed to load affiliate status: ${res.statusText}`);
    }
    return AffiliateStatusResponseSchema.parse(await res.json());
  },

  redeem: async (code: string): Promise<RedeemAffiliateCodeResponse> => {
    const res = await authedFetch("/admin/affiliate/redeem", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
    if (!res.ok) {
      // The route returns the standard error envelope { error: { code, message } }.
      const body = (await res.json().catch(() => null)) as {
        error?: { code?: string; message?: string };
      } | null;
      throw new AffiliateRedeemError(
        (body?.error?.code as RedeemAffiliateCodeError["code"]) ?? "UNKNOWN",
        body?.error?.message ?? res.statusText
      );
    }
    return RedeemAffiliateCodeResponseSchema.parse(await res.json());
  },
};
